import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { getPageData } from 'stile-au/src/utils/getPageData';

import iconDualCodingSvg from 'stile-shared/assets/icons/icon_dual_coding.svg';
import iconElaborationSvg from 'stile-shared/assets/icons/icon_elaboration.svg';
import iconMetacognitionSvg from 'stile-shared/assets/icons/icon_metacognition.svg';
import iconRepetitionSvg from 'stile-shared/assets/icons/icon_repetition.svg';
import iconRetrievalSvg from 'stile-shared/assets/icons/icon_retrieval.svg';
import iconSummarisationSvg from 'stile-shared/assets/icons/icon_summarisation.svg';
import stileXExplainerVideo from 'stile-shared/assets/videos/stile_x_explainers.mp4';

import { HeroBlock } from 'stile-shared/src/components/2020/blocks/HeroBlock';
import { IconCardsBlock } from 'stile-shared/src/components/2020/blocks/IconCardsBlock';
import { TeaserBlock } from 'stile-shared/src/components/2020/blocks/TeaserBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { Link } from 'stile-shared/src/components/2020/common/Link';
import { H2, H3, Ol, P, Section, Ul } from 'stile-shared/src/components/2020/common/Primitives';
import { VideoWrapper } from 'stile-shared/src/components/2020/common/VideoWrapper';
import { Center } from 'stile-shared/src/components/2020/layout/Center';
import { Column } from 'stile-shared/src/components/2020/layout/Column';
import { FullWidthBackground } from 'stile-shared/src/components/2020/layout/FullWidthBackground';
import { theme } from 'stile-shared/src/styles/2020/theme';
import { ReactPlayer } from 'stile-shared/src/utils/loadablePlayers';

import { PageLayout } from 'templates/2020/PageLayout';

import quizDemo from '../../../../stile-shared/assets/images/revision-and-mastery-tools/stile_x_quiz.gif';

export { Head } from 'templates/2020/Head';

function HomePage(props: PageProps) {
  const { hero } = getPageData(props.location.pathname);

  return (
    <PageLayout {...props}>
      <HeroBlock
        {...hero}
        variant="tight"
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/revision-and-mastery-tools/revision_and_mastery_cover.png"
            alt=""
          />
        }
      ></HeroBlock>

      <TextBlock>
        <P>
          <strong>Stile is always evolving.</strong> We’ve supercharged our science resources with
          new revision and note-taking materials to help your students consolidate and master
          coursework, develop intrinsic motivation, and track their progress.
        </P>
        <P>
          For every classroom lesson in Stile, you now have access to carefully scaffolded resources
          that will build your students’ revision and note-taking skills, which become increasingly
          complex as they progress from Year 7 to Year 10. Read more about the{' '}
          <Link openInNewTab url="https://blog.stileeducation.com/stile-x-study-skills/">
            progression of study skills
          </Link>
          .
        </P>
      </TextBlock>

      <FullWidthBackground invertColors>
        <H2>What’s new?</H2>
        <TeaserBlock
          style={{ paddingTop: '60px' }}
          contentSide="right"
          variant="dark"
          image={
            <StaticImage
              src="../../../../stile-shared/assets/images/revision-and-mastery-tools/revision_and_mastery_worksheets.png"
              alt=""
            />
          }
        >
          <H3>Printable revision activities</H3>
          <P>
            Revision activities consolidate key concepts in each lesson with integrated learning
            goals and success criteria. The note-taking tips will support your students in creating
            their own revision notes while implementing effective study skills. As PDFs, you can
            project the worksheet to go through as a class or review the fully worked example
            responses.
          </P>
        </TeaserBlock>

        <TeaserBlock
          variant="dark"
          style={{ paddingTop: '60px' }}
          image={
            <StaticImage
              src="../../../../stile-shared/assets/images/revision-and-mastery-tools/revision_and_mastery_tests.png"
              alt=""
            />
          }
        >
          <H3>Printable practice unit tests</H3>
          <P>
            Practice tests help students study for the real thing and check their understanding
            against learning goals, with model answers for teachers and students.
          </P>
        </TeaserBlock>

        <TeaserBlock
          variant="dark"
          contentSide="right"
          style={{ paddingTop: '60px' }}
          image={
            <StaticImage
              src="../../../../stile-shared/assets/images/revision-and-mastery-tools/revision_and_mastery_flashcards.png"
              alt=""
            />
          }
        >
          <H3>Printable flashcards</H3>
          <P>
            Using the evidence-based learning strategy of spaced repetition, printable flashcards
            will help students define keywords from each unit. You’ll also find flashcards,
            glossaries, and quizzes throughout the Stile X phone app, reinforcing key scientific
            terminology.
          </P>
        </TeaserBlock>

        <TeaserBlock
          variant="dark"
          style={{ paddingTop: '60px' }}
          image={<img src={quizDemo} alt="" />}
        >
          <H3>Glossary Quizzes</H3>
          <P>
            The most popular Stile X app feature is now available in Stile! Using evidence-based
            learning strategies like retrieval practice and spaced repetition, glossary quizzes
            encourage students to build their scientific literacy. When completing a level of the
            quiz, students encounter each glossary term multiple times to encourage mastery.
          </P>
        </TeaserBlock>

        <TeaserBlock
          variant="dark"
          contentSide="right"
          style={{ paddingTop: '60px' }}
          image={
            <VideoWrapper width="200px">
              <ReactPlayer
                url={stileXExplainerVideo}
                width="100%"
                height="100%"
                playing
                playsinline
                loop
                muted
              />
            </VideoWrapper>
          }
        >
          <H3>Video summaries</H3>{' '}
          <P>
            Over 200 60-second summary videos capture curriculum content for each core lesson to
            help students revise (think TikTok meets Khan Academy!).
          </P>
        </TeaserBlock>
      </FullWidthBackground>

      <FullWidthBackground>
        <H2>How to find the resources?</H2>
        <TeaserBlock
          image={
            <StaticImage
              src="../../../../stile-shared/assets/images/revision-and-mastery-tools/revision_and_mastery_location_1.png"
              alt="A close up of a Stile X worksheet, with a hand filling in one of the questions"
            />
          }
        >
          <Column space={theme.space.xxs}>
            <H3>In your subject</H3>
            <P>
              For each of your units, you will find a new section called{' '}
              <strong>Teaching resources</strong> which includes{' '}
              <Link openInNewTab url="https://blog.stileeducation.com/new-stile-teaching-plans/">
                teaching plans
              </Link>
              , lab guides, and parent templates. Within this section, is a brand new section called{' '}
              <strong>Revision resources</strong>. Here you can find:
            </P>
            <Ul space={theme.space.xxxs}>
              <li>
                <strong>Practice tests and model answers</strong>
              </li>
              <li>
                <strong>Printable flashcards</strong>
              </li>
              <li>
                <strong>Glossary quizzes</strong>
              </li>
            </Ul>
          </Column>
        </TeaserBlock>

        <TeaserBlock
          style={{ marginTop: '0px' }}
          image={
            <StaticImage
              src="../../../../stile-shared/assets/images/revision-and-mastery-tools/revision_and_mastery_location_2.png"
              alt="A close up of a Stile X worksheet, with a hand filling in one of the questions"
            />
          }
        >
          <Column space={theme.space.xxs}>
            <H3>In a classroom lesson</H3>
            <P>
              All units in Stile include a{' '}
              <Link
                openInNewTab
                url="https://blog.stileeducation.com/variety-is-the-spice-of-classroom-life-2/"
              >
                variety of lesson types
              </Link>
              . Classroom lessons cover core curriculum requirements and now contain{' '}
              <strong>printable revision activities and example responses</strong>.
            </P>
            <P>You can find them in two places:</P>
            <Ol>
              <li>
                In the <strong>Revision resources</strong> section at the top of a lesson in Prepare
                Mode, so you can print them before class.
              </li>
              <li>
                In the <strong>Revision resources</strong> banner at the bottom of the lesson in
                Prepare and Teach Mode, so that you can project the example responses. Both teachers
                and students can access these resources along with the lesson summary video.
              </li>
            </Ol>
          </Column>
        </TeaserBlock>
      </FullWidthBackground>

      <Section>
        <Center>
          <StaticImage
            src="../../../../stile-shared/assets/images/revision-and-mastery-tools/revision_and_mastery_class_home.png"
            alt="An illustration showing a student working in two locations, one at school with a teacher helping and one at home with her father helping"
          />
        </Center>
      </Section>

      <TextBlock textAlign="center" title="Backed by rigorous cognitive research">
        <P>
          <Link openInNewTab url="https://blog.stileeducation.com/the-science-of-stile-x/">
            Read all about the science of Stile’s revision resources
          </Link>
          , the evidence-based learning strategies embedded into the design and contents.
        </P>
      </TextBlock>

      <IconCardsBlock
        max="1000px"
        cards={[
          {
            icon: { src: iconRetrievalSvg },
            title: 'Retrieval practice',
            text: 'Retrieval practice asks students to actively recall information frequently and in different forms to boost long-term retention and transfer of knowledge to new contexts.',
          },
          {
            icon: { src: iconMetacognitionSvg },
            title: 'Metacognition',
            text: 'Metacognition supports students in monitoring their own learning by encouraging them to evaluate their level of comprehension, task performance, and the efficacy of their study strategies.',
          },
          {
            icon: { src: iconRepetitionSvg },
            title: 'Spaced repetition',
            text: 'Spaced repetition involves revisiting a concept multiple times over a time period to facilitate better understanding and recall.',
          },
          {
            icon: { src: iconSummarisationSvg },
            title: 'Summarisation',
            text: 'Summarisation helps students identify key ideas and relevant details to improve memory and understanding of the concept.',
          },
          {
            icon: { src: iconElaborationSvg },
            title: 'Elaboration',
            text: 'Elaboration is all about adding details to knowledge, for instance connecting to prior knowledge or something personal, to help students deeply understand the concept and more easily remember it.',
          },
          {
            icon: { src: iconDualCodingSvg },
            title: 'Dual coding',
            text: 'Dual coding involves presenting concepts in multiple ways e.g. verbal, visual and textual, to give students multiple pathways to remember, understand and access that knowledge.',
          },
        ]}
      />

      <TeaserBlock
        title="Some things are better on paper"
        contentSide="right"
        image={
          <StaticImage
            aspectRatio={1.8}
            src="../../../../stile-shared/assets/images/revision-and-mastery-tools/revision_and_mastery_paper.jpg"
            alt=""
          />
        }
      >
        <P>
          When taking notes, pen and paper are hard to beat. Research suggests that physical
          note-taking improves cognitive understanding, recall and grades. That’s why we’ve made
          these resources easy to access, download and print. Alongside our interactive digital
          lessons, students can create summary notes, practise their handwriting, and unwind with
          mindful colouring.
        </P>
      </TeaserBlock>

      <TextBlock>
        <P>
          Complete physical booklets for each unit containing these revision activities and practice
          tests can also be purchased from the{' '}
          <Link openInNewTab url="https://shop.stileeducation.com/">
            Stile Shop
          </Link>
          .
        </P>
      </TextBlock>

      <TextBlock>
        <P size="s">
          *Due to existing contracts with Victorian government schools, these revision resources are
          not available within Stile, but booklets can be purchased in Stile. For every purchased
          booklet, the associated revision activities will be available in Stile.
        </P>
      </TextBlock>
    </PageLayout>
  );
}

export default HomePage;
